import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Collapsible,
  Flex,
  Heading,
  Image,
  Input,
  Separator,
  Spinner,
  Stack,
  Text
} from '@chakra-ui/react';
import {
  Briefcase,
  CaretDown,
  CaretUp,
  CheckCircle,
  ClipboardText,
  Clock,
  CurrencyDollar,
  MapPin,
  Warning
} from '@phosphor-icons/react';

import { fetchAPI } from '@/api/fetch';
import {
  apiGetApplicationQuestions,
  apiSubmitApplicationQuestions,
  GetApplicationQuestionsResponse
} from '@/api/services/application_questions.service';

import { Field } from '@/components/ui/field';
import RichEditor from '@/components/forms/RichEditor';

import { ApplicationQuestionAnswer } from '@/types/ApplicationQuestionAnswer';
import { Job } from '@/types/Job';
import { CandidateWithJobContextBasicInfo } from '@/types/Candidate';
import { getEmploymentTypeLabel, getWorkTypeLabel } from '@/types/enums';

import jobBoardBanner from '@/assets/image/job-board-banner.png';

export default function CandidateApplicationQuestionsIndex() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  // Data states
  const [job, setJob] = useState<Job | null>(null);
  const [candidate, setCandidate] = useState<CandidateWithJobContextBasicInfo | null>(null);
  const [allQuestions, setAllQuestions] = useState<ApplicationQuestionAnswer[]>([]);

  // UI states
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isJobDescriptionOpen, setIsJobDescriptionOpen] = useState<boolean>(false);

  // Form states
  const [newAnswers, setNewAnswers] = useState<ApplicationQuestionAnswer[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});

  // Fetch application questions data
  useEffect(() => {
    const fetchData = async () => {
      if (!token) {
        setHasError(true);
        setErrorMessage('Invalid or missing token. Please check your link.');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const response = await fetchAPI<GetApplicationQuestionsResponse>({
          request: () => apiGetApplicationQuestions({ token })
        });

        if (response.success && response.data) {
          const { job_info, candidate_info, application_answers } = response.data;
          setJob(job_info);
          setCandidate(candidate_info);
          setAllQuestions(application_answers);
        } else {
          setHasError(true);
          setErrorMessage(
            response.message || 'Unable to load application questions. Please try again later.'
          );
        }
      } catch (error) {
        setHasError(true);
        setErrorMessage('Unable to load application questions. Please try again later.');
        console.error('Error fetching application questions:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [token]);

  // Determine answered and unanswered questions (memoized to avoid recalculation)
  const answeredQuestions = useMemo(
    () => allQuestions.filter((q) => q.answer !== ''),
    [allQuestions]
  );

  const unansweredQuestions = useMemo(
    () => allQuestions.filter((q) => q.answer === ''),
    [allQuestions]
  );

  const allQuestionsAnswered = useMemo(
    () => unansweredQuestions.length === 0 && allQuestions.length > 0,
    [unansweredQuestions.length, allQuestions.length]
  );

  // Get answer for a question (memoized callbacks)
  const getAnswerValue = useCallback(
    (questionId: string): string => {
      const answer = newAnswers.find((a) => a.question_id === questionId);
      return answer?.answer || '';
    },
    [newAnswers]
  );

  const getExistingAnswer = useCallback(
    (questionId: string): string => {
      const answer = allQuestions.find((a) => a.question_id === questionId);
      return answer?.answer || '';
    },
    [allQuestions]
  );

  // Handle answer change
  const handleAnswerChange = useCallback(
    (questionId: string, answer: string) => {
      const question = unansweredQuestions.find((q) => q.question_id === questionId);
      if (!question) return;

      setNewAnswers((prev) => {
        const newAnswersList = [...prev];
        const existingIndex = newAnswersList.findIndex((a) => a.question_id === questionId);

        const answerObj: ApplicationQuestionAnswer = {
          question_id: questionId,
          question: question.question,
          question_type: question.question_type,
          options: question.options,
          required: question.required,
          answer
        };

        if (existingIndex >= 0) {
          newAnswersList[existingIndex] = answerObj;
        } else {
          newAnswersList.push(answerObj);
        }

        return newAnswersList;
      });

      // Clear validation error for this question
      setValidationErrors((prev) => {
        if (!prev[questionId]) return prev;
        const newErrors = { ...prev };
        delete newErrors[questionId];
        return newErrors;
      });
    },
    [unansweredQuestions]
  );

  // Validate and submit
  const handleSubmit = useCallback(async () => {
    const newErrors: { [key: string]: string } = {};

    // Validate all required unanswered questions
    unansweredQuestions.forEach((question) => {
      if (question.required) {
        const answer = getAnswerValue(question.question_id);
        if (!answer || answer.trim() === '') {
          newErrors[question.question_id] = 'This question is required';
        }
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setValidationErrors(newErrors);
      setSubmitError('Please answer all required questions before submitting.');
      return;
    }

    if (newAnswers.length === 0) {
      setSubmitError('Please answer at least one question before submitting.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await fetchAPI<void>({
        request: () =>
          apiSubmitApplicationQuestions({
            token: token!,
            payload: {
              application_answers: newAnswers
            }
          })
      });

      if (response.success) {
        setSubmitSuccess(true);
        // Move new answers to existing answers to update the UI state
        setAllQuestions((prev) => [...prev, ...newAnswers]);
        setNewAnswers([]);
        setValidationErrors({});
      } else {
        setSubmitError(response.message || 'Failed to submit answers. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting answers:', error);
      setSubmitError('Failed to submit answers. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [unansweredQuestions, getAnswerValue, newAnswers, token]);

  // Helper functions for formatting (memoized)
  const formatSalary = useMemo(() => {
    if (!job) return '';
    if (!job.salary_from && !job.salary_to) return '';

    if (job.salary_from && job.salary_to) {
      return `${
        job.currency
      } ${job.salary_from.toLocaleString()} - ${job.salary_to.toLocaleString()}`;
    }
    if (job.salary_from) {
      return `${job.currency} ${job.salary_from.toLocaleString()}+`;
    }
    return `${job.currency} ${job.salary_to?.toLocaleString()}`;
  }, [job]);

  // Format phone number for display
  const formatPhoneNumber = useCallback((phone: string) => {
    if (!phone) return '';

    // Remove all non-digit characters
    const cleaned = phone.replace(/\D/g, '');

    // Format based on length
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      // US number with country code: +1 (123) 456-7890
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    } else if (cleaned.length === 10) {
      // US number without country code: (123) 456-7890
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length > 10) {
      // International number: display with country code
      const countryCode = cleaned.slice(0, cleaned.length - 10);
      const number = cleaned.slice(-10);
      return `+${countryCode} (${number.slice(0, 3)}) ${number.slice(3, 6)}-${number.slice(6)}`;
    }

    // Return original if unable to format
    return phone;
  }, []);

  // Loading state
  if (isLoading) {
    return (
      <Flex justify="center" align="center" minH="100vh" bg="background.primary">
        <Spinner size="xl" />
      </Flex>
    );
  }

  // Error state
  if (hasError || !job || !candidate) {
    return (
      <Flex bg="background.primary" minH="100vh" w="100%" justify="center" align="center">
        <Box maxW="600px" w="100%" p="24px">
          <Box
            bg="background.secondary"
            border="1px solid"
            borderColor="error.300"
            borderRadius="12px"
            p="48px"
            textAlign="center"
          >
            <Flex justify="center" mb="24px">
              <Box
                bg="error.100"
                borderRadius="full"
                p="20px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                color="error.700"
              >
                <Warning size={48} weight="fill" />
              </Box>
            </Flex>
            <Heading size="xl" color="neutrals.900" mb="16px">
              Unable to Load Application Questions
            </Heading>
            <Text fontSize="16px" color="neutrals.700" mb="8px" lineHeight="1.6">
              {errorMessage}
            </Text>
            <Text fontSize="14px" color="neutrals.500" mt="24px">
              If you believe this is an error, please contact the hiring team or support.
            </Text>
          </Box>
        </Box>
      </Flex>
    );
  }

  // Submit success - Show dedicated success page
  if (submitSuccess) {
    return (
      <Flex
        bg="background.primary"
        minH="100vh"
        w="100%"
        justify="center"
        align="center"
        direction="column"
        position="relative"
      >
        <Box maxW="700px" w="100%" p="24px" position="relative" zIndex={1}>
          <Box p="48px" textAlign="center">
            <Flex justify="center" mb="24px">
              <Box
                bg="success.100"
                borderRadius="full"
                p="24px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                color="success.500"
              >
                <CheckCircle size={64} weight="fill" />
              </Box>
            </Flex>
            <Heading size="2xl" color="success.700" mb="16px">
              Answers Submitted Successfully!
            </Heading>
            <Text fontSize="18px" color="neutrals.700" mb="32px" lineHeight="1.7">
              Thank you for completing the application questions for{' '}
              <Text as="span" fontWeight="semibold" color="neutrals.900">
                {job.job_title}
              </Text>
              . Your responses have been recorded.
            </Text>
            <Text fontSize="14px" color="neutrals.500" fontStyle="italic">
              You may now close this page.
            </Text>
          </Box>
        </Box>

        {/* Fixed Footer */}
        <Box
          as="footer"
          bg="transparent"
          w="100%"
          h="56px"
          position="fixed"
          left={0}
          bottom={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
          zIndex={2}
        >
          <Text fontSize="14px" color="neutrals.700" textAlign="center" w="100%">
            Powered by{' '}
            <Text as="span" color="primary.900" fontWeight="bold">
              <a href="https://www.polarisjobs.com" target="_blank" rel="noreferrer">
                PolarisJobs
              </a>
            </Text>
          </Text>
        </Box>
      </Flex>
    );
  }

  // All questions completed - Show dedicated completion page
  if (allQuestionsAnswered) {
    return (
      <Flex
        bg="background.primary"
        minH="100vh"
        w="100%"
        justify="center"
        align="center"
        direction="column"
        position="relative"
      >
        <Box maxW="700px" w="100%" p="24px" position="relative" zIndex={1}>
          <Box p="48px" textAlign="center">
            <Flex justify="center" mb="24px">
              <Box
                bg="success.100"
                borderRadius="full"
                p="24px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                color="success.500"
              >
                <CheckCircle size={64} weight="fill" />
              </Box>
            </Flex>
            <Heading size="2xl" color="success.700" mb="16px">
              All Questions Completed!
            </Heading>
            <Text fontSize="18px" color="neutrals.700" mb="32px" lineHeight="1.7">
              You have successfully answered all application questions for{' '}
              <Text as="span" fontWeight="semibold" color="neutrals.900">
                {job.job_title}
              </Text>
              .
            </Text>
            <Text fontSize="14px" color="neutrals.500" fontStyle="italic">
              Thank you for your interest in this position. You may now close this page.
            </Text>
          </Box>
        </Box>

        {/* Fixed Footer */}
        <Box
          as="footer"
          bg="transparent"
          w="100%"
          h="56px"
          position="fixed"
          left={0}
          bottom={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
          zIndex={2}
        >
          <Text fontSize="14px" color="neutrals.700" textAlign="center" w="100%">
            Powered by{' '}
            <Text as="span" color="primary.900" fontWeight="bold">
              <a href="https://www.polarisjobs.com" target="_blank" rel="noreferrer">
                PolarisJobs
              </a>
            </Text>
          </Text>
        </Box>
      </Flex>
    );
  }

  return (
    <Flex bg="background.primary" minH="100vh" w="100%" direction="column">
      <Flex direction="column" maxW="1200px" mx="auto">
        {/* Banner */}
        <Image src={jobBoardBanner} alt="Job Board Banner" w="100%" h="320px" objectFit="cover" />

        <Stack p="32px 120px 64px 120px" w="80%" maxW="1000px" mx="auto">
          {/* Title */}
          <Text fontSize="32px" fontWeight="bold" color="neutrals.900" mb="32px">
            Additional Questions for Your Application
          </Text>

          {/* Job Header */}
          <Stack px="16px" gap="16px" mt="8px" mb="32px">
            <Heading size="2xl" color="neutrals.900">
              {job.job_title}
            </Heading>

            <Flex justify="space-between" flexWrap="wrap" mt="8px" maxW="600px">
              <Stack mb="12px">
                <Flex align="center" gap="8px" color="neutrals.600">
                  <MapPin size={20} />
                  <Text fontSize="md">{job.location}</Text>
                </Flex>

                <Flex align="center" gap="8px" color="neutrals.600">
                  <Briefcase size={20} />
                  <Text fontSize="md">{getWorkTypeLabel(job.work_type)}</Text>
                </Flex>
              </Stack>

              <Stack>
                {formatSalary && (
                  <Flex align="center" gap="8px" color="neutrals.600">
                    <CurrencyDollar size={16} />
                    <Text fontSize="md">{formatSalary}</Text>
                  </Flex>
                )}

                <Flex align="center" gap="8px" color="neutrals.600">
                  <Clock size={20} />
                  <Text fontSize="md">{getEmploymentTypeLabel(job.employment_type)}</Text>
                </Flex>
              </Stack>
            </Flex>
          </Stack>

          {/* Job Description */}
          <Box mb="40px">
            <Collapsible.Root
              open={isJobDescriptionOpen}
              onOpenChange={({ open }) => setIsJobDescriptionOpen(open)}
            >
              <Box overflow="hidden">
                <Collapsible.Trigger
                  w="100%"
                  px="16px"
                  py="12px"
                  cursor="pointer"
                  bg="neutrals.50"
                  _hover={{ bg: 'neutrals.100' }}
                  borderRadius="12px"
                >
                  <Flex justifyContent="space-between" alignItems="center" w="100%">
                    <Heading fontSize="24px" color="neutrals.900">
                      Job Description
                    </Heading>
                    {isJobDescriptionOpen ? (
                      <CaretUp size={24} color="#242424" />
                    ) : (
                      <CaretDown size={24} color="#242424" />
                    )}
                  </Flex>
                </Collapsible.Trigger>

                {/* Preview when collapsed */}
                {!isJobDescriptionOpen && (
                  <Box px="32px" py="28px">
                    <Box
                      dangerouslySetInnerHTML={{ __html: job.description }}
                      color="neutrals.600"
                      fontSize="16px"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      css={{
                        display: '-webkit-box',
                        WebkitLineClamp: 10,
                        WebkitBoxOrient: 'vertical',
                        '& p': {
                          margin: 0
                        },
                        '& ul, & ol': {
                          marginLeft: '20px'
                        }
                      }}
                    />

                    {job.description && (
                      <Box mt="16px">
                        <Button
                          fontSize="14px"
                          variant="ghost"
                          color="neutrals.500"
                          p="0"
                          fontStyle="italic"
                          _hover={{ bg: 'transparent', textDecoration: 'underline' }}
                          onClick={() => setIsJobDescriptionOpen(true)}
                        >
                          Show full job description...
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}

                <Collapsible.Content>
                  <Box px="16px" py="12px">
                    <RichEditor
                      key="job-description"
                      content={job.description}
                      type="job-description"
                      readOnly
                    />
                  </Box>
                </Collapsible.Content>
              </Box>
            </Collapsible.Root>
          </Box>

          <Separator mb="32px" />

          {/* Candidate Information Section */}
          {candidate && (
            <Box px="32px" mb="32px">
              <Heading fontSize="24px" mb="16px" color="neutrals.900">
                Your Information
              </Heading>
              <Flex justify="space-between" flexWrap="wrap" mt="8px" maxW="600px">
                <Stack gap="12px">
                  <Text fontSize="16px" color="neutrals.500" fontWeight="medium">
                    Name
                  </Text>
                  <Text fontSize="16px" color="neutrals.500" fontWeight="medium">
                    Email
                  </Text>
                  <Text fontSize="16px" color="neutrals.500" fontWeight="medium">
                    Phone
                  </Text>
                  {candidate.linkedin && (
                    <Text fontSize="16px" color="neutrals.500" fontWeight="medium">
                      LinkedIn
                    </Text>
                  )}
                </Stack>

                <Stack gap="12px">
                  <Text fontSize="16px" color="neutrals.900">
                    {candidate.first_name} {candidate.last_name}
                  </Text>
                  <Text fontSize="16px" color="neutrals.700">
                    <a href={`mailto:${candidate.email}`}>{candidate.email}</a>
                  </Text>
                  <Text fontSize="16px" color="neutrals.700">
                    <a href={`tel:${candidate.phone}`}>{formatPhoneNumber(candidate.phone)}</a>
                  </Text>
                  {candidate.linkedin && (
                    <Text fontSize="16px" color="neutrals.700">
                      <a href={candidate.linkedin} target="_blank" rel="noreferrer">
                        {candidate.linkedin}
                      </a>
                    </Text>
                  )}
                </Stack>
              </Flex>
            </Box>
          )}

          {/* Answered Questions - Read Only */}
          {answeredQuestions.length > 0 && (
            <Box px="32px" mb="32px">
              <Flex align="center" gap="12px" mb="24px">
                <Box color="success.500">
                  <CheckCircle size={28} weight="fill" />
                </Box>
                <Heading fontSize="24px" color="neutrals.900" fontWeight="semibold">
                  Answered Questions
                </Heading>
              </Flex>
              <Stack gap="20px">
                {answeredQuestions.map((question, index) => (
                  <Box key={question.question_id}>
                    <Flex alignItems="center" gap="12px" mb="8px">
                      <Text fontSize="16px" fontWeight="semibold" color="neutrals.700">
                        Question {index + 1}
                      </Text>
                    </Flex>
                    <Text fontSize="16px" color="neutrals.900" mb="12px">
                      {question.question}
                    </Text>
                    <Box
                      bg="neutrals.100"
                      border="1px solid"
                      borderColor="neutrals.300"
                      borderRadius="8px"
                      p="16px"
                    >
                      <Text fontSize="16px" color="neutrals.900" fontWeight="medium">
                        {getExistingAnswer(question.question_id)}
                      </Text>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {/* Unanswered Questions - Editable */}
          {unansweredQuestions.length > 0 && (
            <Box px="32px" mb="40px">
              <Flex align="center" gap="12px" mb="16px">
                <Box color="primary.900">
                  <ClipboardText size={28} weight="fill" />
                </Box>
                <Heading fontSize="24px" color="neutrals.900" fontWeight="semibold">
                  Additional Questions
                </Heading>
              </Flex>
              <Text fontSize="16px" color="neutrals.600" mb="32px">
                Please answer the following questions to complete your application.
              </Text>

              <Stack gap="32px">
                {unansweredQuestions.map((question, index) => (
                  <Box key={question.question_id}>
                    <Flex alignItems="center" gap="12px" mb="8px">
                      <Text fontSize="16px" fontWeight="semibold" color="neutrals.700">
                        Question {answeredQuestions.length + index + 1}
                      </Text>
                    </Flex>
                    <Text fontSize="16px" fontWeight="medium" color="neutrals.900" mb="12px">
                      {question.question}
                      {question.required && (
                        <Box as="span" color="error.500" ml="4px">
                          *
                        </Box>
                      )}
                    </Text>

                    {/* Text Question */}
                    {question.question_type === 'text' && (
                      <Field
                        invalid={!!validationErrors[question.question_id]}
                        errorText={
                          <Box fontSize="14px" color="error.700">
                            {validationErrors[question.question_id]}
                          </Box>
                        }
                      >
                        <Input
                          size="lg"
                          borderRadius="8px"
                          placeholder="Enter your answer"
                          bg="background.secondary"
                          value={getAnswerValue(question.question_id)}
                          onChange={(e) => handleAnswerChange(question.question_id, e.target.value)}
                          disabled={isSubmitting}
                        />
                      </Field>
                    )}

                    {/* Yes/No Question */}
                    {question.question_type === 'yes_no' && (
                      <Stack gap="12px">
                        <Flex gap="16px">
                          <Button
                            variant={
                              getAnswerValue(question.question_id) === 'yes' ? 'solid' : 'outline'
                            }
                            onClick={() => handleAnswerChange(question.question_id, 'yes')}
                            disabled={isSubmitting}
                            bg={
                              getAnswerValue(question.question_id) === 'yes'
                                ? 'primary.900'
                                : 'background.secondary'
                            }
                            color={
                              getAnswerValue(question.question_id) === 'yes'
                                ? 'white'
                                : 'neutrals.700'
                            }
                            border="1px solid"
                            borderRadius="8px"
                            borderColor="neutrals.300"
                            fontSize="16px"
                            _hover={{
                              bg:
                                getAnswerValue(question.question_id) === 'yes'
                                  ? 'primary.700'
                                  : 'neutrals.100'
                            }}
                          >
                            Yes
                          </Button>
                          <Button
                            variant={
                              getAnswerValue(question.question_id) === 'no' ? 'solid' : 'outline'
                            }
                            onClick={() => handleAnswerChange(question.question_id, 'no')}
                            disabled={isSubmitting}
                            bg={
                              getAnswerValue(question.question_id) === 'no'
                                ? 'primary.900'
                                : 'background.secondary'
                            }
                            color={
                              getAnswerValue(question.question_id) === 'no'
                                ? 'white'
                                : 'neutrals.700'
                            }
                            border="1px solid"
                            borderRadius="8px"
                            borderColor="neutrals.300"
                            fontSize="16px"
                            _hover={{
                              bg:
                                getAnswerValue(question.question_id) === 'no'
                                  ? 'primary.700'
                                  : 'neutrals.100'
                            }}
                          >
                            No
                          </Button>
                        </Flex>
                        {validationErrors[question.question_id] && (
                          <Text fontSize="14px" color="error.700">
                            {validationErrors[question.question_id]}
                          </Text>
                        )}
                      </Stack>
                    )}

                    {/* Multiple Choice Question */}
                    {question.question_type === 'multiple_choice' && question.options && (
                      <Stack gap="12px">
                        {question.options.map((option) => (
                          <Button
                            key={option}
                            onClick={() => handleAnswerChange(question.question_id, option)}
                            disabled={isSubmitting}
                            variant={
                              getAnswerValue(question.question_id) === option ? 'solid' : 'outline'
                            }
                            border="1px solid"
                            borderColor={
                              getAnswerValue(question.question_id) === option
                                ? 'primary.600'
                                : 'neutrals.300'
                            }
                            borderRadius="8px"
                            bg={
                              getAnswerValue(question.question_id) === option
                                ? 'primary.50'
                                : 'background.secondary'
                            }
                            color={
                              getAnswerValue(question.question_id) === option
                                ? 'primary.900'
                                : 'neutrals.700'
                            }
                            fontWeight={
                              getAnswerValue(question.question_id) === option ? 'medium' : 'normal'
                            }
                            textAlign="left"
                            transition="all 0.2s"
                            w="100%"
                            p="24px 16px"
                            _hover={{
                              bg:
                                getAnswerValue(question.question_id) === option
                                  ? 'primary.100'
                                  : 'neutrals.100'
                            }}
                            cursor={isSubmitting ? 'not-allowed' : 'pointer'}
                            justifyContent="flex-start"
                          >
                            <Text fontSize="16px">{option}</Text>
                          </Button>
                        ))}
                        {validationErrors[question.question_id] && (
                          <Text fontSize="14px" color="error.700">
                            {validationErrors[question.question_id]}
                          </Text>
                        )}
                      </Stack>
                    )}
                  </Box>
                ))}
              </Stack>

              {/* Submit Error */}
              {submitError && (
                <Box mt="24px">
                  <Text fontSize="16px" color="error.700">
                    {submitError}
                  </Text>
                </Box>
              )}

              {/* Submit Button */}
              <Flex justify="center" mt="32px">
                <Button
                  size="lg"
                  bg="primary.900"
                  color="white"
                  borderRadius="8px"
                  onClick={handleSubmit}
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  _hover={{ bg: 'primary.700' }}
                  p="24px 48px"
                  fontSize="16px"
                  fontWeight="medium"
                >
                  Submit
                </Button>
              </Flex>
            </Box>
          )}
        </Stack>

        {/* Footer */}
        <Box as="footer" bg="background.primary" h="40px" alignContent="center">
          <Text fontSize="14px" color="neutrals.700" textAlign="center">
            Powered by{' '}
            <Text as="span" color="primary.900" fontWeight="bold">
              <a href="https://www.polarisjobs.com" target="_blank" rel="noreferrer">
                PolarisJobs
              </a>
            </Text>
          </Text>
        </Box>
      </Flex>
    </Flex>
  );
}
